export interface GeocodedAddress {
  lat: number;
  lng: number;
  address: string;
  placeId?: string;
  source: 'google' | 'nominatim';
}

const GOOGLE_GEOCODING_API_KEY = 'AIzaSyA5Tuo-9Uj_uyaMgtUjK20kbDZqqjf2GQw';

function isValidCoordinate(lat: number, lng: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
}

async function geocodeWithGoogle(query: string): Promise<GeocodedAddress | null> {
  if (!GOOGLE_GEOCODING_API_KEY || !query.trim()) return null;

  const response = await fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${GOOGLE_GEOCODING_API_KEY}`,
  );
  const json = await response.json();
  const result = Array.isArray(json?.results) ? json.results[0] : null;
  const lat = Number(result?.geometry?.location?.lat);
  const lng = Number(result?.geometry?.location?.lng);

  if (!result || !isValidCoordinate(lat, lng)) return null;

  return {
    lat,
    lng,
    address: String(result.formatted_address || query),
    placeId: typeof result.place_id === 'string' ? result.place_id : undefined,
    source: 'google',
  };
}

async function geocodeWithNominatim(query: string): Promise<GeocodedAddress | null> {
  if (!query.trim()) return null;

  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&addressdetails=1&accept-language=en`,
    { headers: { 'User-Agent': 'TonyteApp/1.0' } },
  );
  const json = await response.json();
  const result = Array.isArray(json) ? json[0] : null;
  const lat = Number(result?.lat);
  const lng = Number(result?.lon);

  if (!result || !isValidCoordinate(lat, lng)) return null;

  return {
    lat,
    lng,
    address: String(result.display_name || query),
    placeId: result.place_id ? `nominatim:${result.place_id}` : undefined,
    source: 'nominatim',
  };
}

export async function geocodeAddress(query: string): Promise<GeocodedAddress | null> {
  const trimmed = query.trim();
  if (!trimmed) return null;

  try {
    const googleResult = await geocodeWithGoogle(trimmed);
    if (googleResult) return googleResult;
  } catch (error) {
    console.warn('[Tonyte][RN][Geocode] Google geocode failed', {
      query: trimmed,
      error,
    });
  }

  try {
    return await geocodeWithNominatim(trimmed);
  } catch (error) {
    console.warn('[Tonyte][RN][Geocode] Nominatim geocode failed', {
      query: trimmed,
      error,
    });
    return null;
  }
}
